import type { User } from '@aiptyg/types';

export const ADMIN_PERMISSION_ALL = '*';

interface AdminPermissionMeta {
  key: string;
  label: string;
  path?: string;
  hidden?: boolean;
}

export const ADMIN_PERMISSIONS: readonly AdminPermissionMeta[] = [
  { key: 'dashboard', label: '首页', path: '/' },
  { key: 'settings', label: '系统设置', path: '/settings/invitation-policy' },
  { key: 'admin_accounts', label: '后台账号', path: '/admin-accounts' },
  { key: 'users', label: '用户管理', path: '/users' },
  { key: 'agents.view', label: '商户-查看', path: '/agents' },
  { key: 'agents.manage', label: '商户-管理与账号重置' },
  { key: 'agent_tasks.view', label: '商户任务-查看', path: '/agent-tasks' },
  { key: 'agent_tasks.review', label: '商户任务-发布审核', path: '/agent-task-reviews' },
  { key: 'agent_tasks.control', label: '商户任务-控制与展示设置' },
  { key: 'agent_evidence.view', label: '商户提交记录' },
  { key: 'agent_evidence.original.view', label: '商户任务-原图查看' },
  { key: 'agent_reports.view', label: '商户-在线报表', path: '/agent-reports' },
  { key: 'agent_logs.view', label: '商户-审计日志', path: '/agent-audit' },
  { key: 'agent_finance.view', label: '商户资金-查看', path: '/agent-finance' },
  { key: 'agent_finance.recharge', label: '商户资金-充值' },
  { key: 'agent_finance.deduct', label: '商户资金-平账扣款' },
  { key: 'agent_finance.settle', label: '商户资金-人工结算' },
  { key: 'agent_finance.settings', label: '商户资金-结算设置' },
  { key: 'advertising.view', label: '广告-查看', path: '/advertising' },
  { key: 'advertising.manage', label: '广告-管理与广告位配置', path: '/advertising/slots' },
  { key: 'advertising.review', label: '广告-发布审核', path: '/advertising/reviews' },
  { key: 'advertising.reports.view', label: '广告-统计报表', path: '/advertising/reports' },
  { key: 'tasks', label: '任务管理', path: '/tasks' },
  { key: 'assignments.view', label: '会员提交审核-查看', path: '/assignments' },
  { key: 'assignments.review', label: '会员提交审核-处理' },
  { key: 'withdrawals.view', label: '提现管理-查看', path: '/withdrawals' },
  { key: 'withdrawals.review', label: '提现管理-审核' },
  { key: 'withdrawals.remark', label: '提现管理-备注' },
  { key: 'withdrawals.settle', label: '提现管理-标记到账' },
  { key: 'notifications', label: '消息通知', path: '/notifications' },
  { key: 'support_feedback', label: '客服反馈', path: '/support-feedback' },
  { key: 'invitation', label: '邀请码管理', path: '/invitation/codes' },
  { key: 'rewards', label: '奖励记录', path: '/rewards' },
  { key: 'commissions', label: '邀请奖励记录', path: '/commissions' },
  { key: 'team', label: '团队查询', path: '/team' },
  { key: 'system_logs', label: '系统日志', path: '/system-logs' },
  { key: 'assignments', label: '任务审核（旧权限）', hidden: true },
  { key: 'withdrawals', label: '提现模块（旧权限）', hidden: true },
] as const;

export type AdminPermissionKey = string;

export const ADMIN_ACCOUNT_PERMISSION_OPTIONS = ADMIN_PERMISSIONS.filter((item) => !item.hidden);

function expandAdminPermission(permission: string, result: Set<string>) {
  switch (permission) {
    case 'agent_finance.recharge':
    case 'agent_finance.deduct':
    case 'agent_finance.settle':
    case 'agent_finance.settings':
      result.add('agent_finance.view'); result.add(permission); return;
    case 'advertising.manage':
    case 'advertising.review':
    case 'advertising.reports.view':
      result.add('advertising.view'); result.add(permission); return;
    case 'agents.manage':
      result.add('agents.view'); result.add(permission); return;
    case 'agent_tasks.review':
    case 'agent_tasks.control':
      result.add('agent_tasks.view'); result.add(permission); return;
    case 'agent_evidence.original.view':
      result.add('agent_evidence.view'); result.add(permission); return;
    case 'assignments':
      result.add('assignments');
      result.add('assignments.view');
      result.add('assignments.review');
      return;
    case 'assignments.review':
      result.add('assignments.view');
      result.add('assignments.review');
      return;
    case 'withdrawals':
      result.add('withdrawals');
      result.add('withdrawals.view');
      result.add('withdrawals.review');
      result.add('withdrawals.remark');
      result.add('withdrawals.settle');
      return;
    case 'withdrawals.review':
      result.add('withdrawals.view');
      result.add('withdrawals.review');
      return;
    case 'withdrawals.remark':
      result.add('withdrawals.view');
      result.add('withdrawals.remark');
      return;
    case 'withdrawals.settle':
      result.add('withdrawals.view');
      result.add('withdrawals.settle');
      return;
    default:
      if (permission) {
        result.add(permission);
      }
  }
}

export function isFullAdmin(user: User | null): boolean {
  if (!user || user.role !== 'admin') return false;
  const permissions = user.adminPermissions ?? [];
  return permissions.length === 0 || permissions.includes(ADMIN_PERMISSION_ALL);
}

export function hasAdminPermission(user: User | null, permission: AdminPermissionKey): boolean {
  if (!user || user.role !== 'admin') return false;
  if (isFullAdmin(user)) return true;
  const expanded = new Set<string>();
  for (const item of user.adminPermissions ?? []) {
    expandAdminPermission(item, expanded);
  }
  return expanded.has(permission);
}

export function getFirstAdminPath(user: User | null): string {
  const item = ADMIN_PERMISSIONS.find((permission) => permission.path && hasAdminPermission(user, permission.key));
  return item?.path ?? '/login';
}

export function getPermissionLabel(permission: string): string {
  return ADMIN_PERMISSIONS.find((item) => item.key === permission)?.label ?? permission;
}
